/*  MUI  */
import { Box, Typography } from '@mui/material';

/*  Shared  */
import { Logo } from 'application/Shared/Components/App';
import { getDateTime } from 'application/Shared/Components/Core';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, SessionHelper } from 'application/Shared/Helpers';
import { Client } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  Images  */
import AccountsInventoryDark from 'images/reports/dark/AccountsInventory.png';
import AdoptionDark from 'images/reports/dark/Adoption.png';
import BrowserDark from 'images/reports/dark/Browser.png';
import CBRDark from 'images/reports/dark/CBR.png';
import ContractsDark from 'images/reports/dark/Contracts.png';
import DiscoveryDark from 'images/reports/dark/Discovery.png';
import MicrosoftDark from 'images/reports/dark/Microsoft.png';
import OffboardDark from 'images/reports/dark/Offboard.png';
import OwnerDark from 'images/reports/dark/Owner.png';
import PartnerUsersDark from 'images/reports/dark/PartnerUsers.png';
import QBRDark from 'images/reports/dark/QBR.png';
import UsageDark from 'images/reports/dark/Usage.png';
import UserApplicationInventoryDark from 'images/reports/dark/UserApplicationInventory.png';
import VendorIncidentDark from 'images/reports/dark/VendorIncident.png';
import AccountsInventoryLight from 'images/reports/light/AccountsInventory.png';
import AdoptionLight from 'images/reports/light/Adoption.png';
import BrowserLight from 'images/reports/light/Browser.png';
import CBRLight from 'images/reports/light/CBR.png';
import ContractsLight from 'images/reports/light/Contracts.png';
import DiscoveryLight from 'images/reports/light/Discovery.png';
import MicrosoftLight from 'images/reports/light/Microsoft.png';
import OffboardLight from 'images/reports/light/Offboard.png';
import OwnerLight from 'images/reports/light/Owner.png';
import PartnerUsersLight from 'images/reports/light/PartnerUsers.png';
import QBRLight from 'images/reports/light/QBR.png';
import UsageLight from 'images/reports/light/Usage.png';
import UserApplicationInventoryLight from 'images/reports/light/UserApplicationInventory.png';
import VendorIncidentLight from 'images/reports/light/VendorIncident.png';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

class CoverPage extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            client: {
                name: ''
            },
            image: null,
            loaded: false,
            settings: {
                city: '',
                name: '',
                phone: '',
                qbrToCBR: false,
                state: '',
                streetAddress: '',
                zip: ''
            }
        };

        const { clientId } = props.match.params;
        /* requests */
        this.Client = new Client(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { match, session, reportSetup, tenants } = this.props;
        const { type } = reportSetup;

        const { partnerId } = match.params;
        const partnerTenantInfo = tenants[partnerId];
        const partnerTenant = partnerTenantInfo?.tenant;
        const partnerTenantSettings = partnerTenant?.settings;

        const sessionHelper = new SessionHelper(session);
        // Use the viewed partner tenant's settings for branding. If no settings are found, fallback to session settings.
        // TODO: Maybe we shouldn't fallback to session tenant settings?
        const settings =
            partnerTenantSettings || sessionHelper.getTenantSettings();

        if (Helpers.isEmpty(settings)) {
            debug.e('No Tenant Settings Found');
        }

        const client = await this.Client.get();
        if (!client) {
            debug.e('Unable to get Tenant', client);
            return;
        }

        const userTheme = getUserTheme(session?.user?.theme);

        let image = '';
        switch (type) {
            case 'accountsInventory':
                image =
                    userTheme === 'dark'
                        ? AccountsInventoryDark
                        : AccountsInventoryLight;
                break;
            case 'businessOwner':
                image = userTheme === 'dark' ? OwnerDark : OwnerLight;
                break;
            case 'browser':
                image = userTheme === 'dark' ? BrowserDark : BrowserLight;
                break;
            case 'contracts':
                image = userTheme === 'dark' ? ContractsDark : ContractsLight;
                break;
            case 'discovery':
                image = userTheme === 'dark' ? DiscoveryDark : DiscoveryLight;
                break;
            case 'offboard':
                image = userTheme === 'dark' ? OffboardDark : OffboardLight;
                break;
            case 'microsoftOfficeOverlap':
                image = userTheme === 'dark' ? MicrosoftDark : MicrosoftLight;
                break;
            case 'organizational':
                image = userTheme === 'dark' ? UsageDark : UsageLight;
                break;
            case 'partnerUsers':
                image =
                    userTheme === 'dark' ? PartnerUsersDark : PartnerUsersLight;
                break;
            case 'productAdoption':
                image = userTheme === 'dark' ? AdoptionDark : AdoptionLight;
                break;
            case 'qbr':
                if (settings.qbrToCBR) {
                    image = userTheme === 'dark' ? CBRDark : CBRLight;
                } else {
                    image = userTheme === 'dark' ? QBRDark : QBRLight;
                }
                break;
            case 'partnerBreachImpact':
                image =
                    userTheme === 'dark'
                        ? VendorIncidentDark
                        : VendorIncidentLight;
                break;
            case 'userApps':
                image =
                    userTheme === 'dark'
                        ? UserApplicationInventoryDark
                        : UserApplicationInventoryLight;
                break;
            default:
                debug.e(`Unable to find Image for ${type}`);
                return;
        }

        this.setState({ client, image, loaded: true, settings });
    };

    unload = () => {
        this.Client.cancel();
    };

    renderPreparedBy = () => {
        const { t } = this.props;
        const { settings } = this.state;
        const { name, phone, streetAddress, city, zip } = settings;

        return (
            <Box data-testid="renderPreparedBy">
                <Typography
                    color="text.coverPage.title"
                    sx={{
                        fontSize: {
                            lg: FontSizes[4],
                            xs: FontSizes[3]
                        },
                        fontWeight: 600
                    }}
                >
                    {t('Common.prepared_by')}
                </Typography>
                <Box
                    display="flex"
                    flexDirection="column"
                    paddingTop="16px"
                    sx={{ color: 'text.coverPage.text' }}
                >
                    {name && (
                        <Typography sx={{ fontWeight: 600 }}>{name}</Typography>
                    )}
                    {phone && <Typography>{settings.phone}</Typography>}
                    {streetAddress && (
                        <Typography>{settings.streetAddress}</Typography>
                    )}
                    {(city || settings.state || zip) && (
                        <Typography>{`${city}, ${settings.state} ${zip}`}</Typography>
                    )}
                    <Box display={['none', 'initial']}>
                        <Logo
                            containerStyle={{ marginBottom: 0 }}
                            checkTenantSettings
                            style={{
                                marginTop: '16px',
                                maxHeight: '80px',
                                maxWidth: '150px'
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

    renderPreparedFor = () => {
        const { t, reportSetup } = this.props;
        const { client } = this.state;
        const { clientName } = reportSetup;

        const name = clientName || client.name;

        return (
            <Box data-testid="renderPreparedFor">
                <Typography
                    color="text.coverPage.title"
                    sx={{
                        fontSize: {
                            lg: FontSizes[4],
                            xs: FontSizes[3]
                        },
                        fontWeight: 600
                    }}
                >
                    {t('Common.prepared_for')}
                </Typography>
                <Box paddingTop="16px" sx={{ color: 'text.coverPage.text' }}>
                    <Typography sx={{ fontWeight: 600 }}>{name}</Typography>
                    <Box>
                        {t('Common.prepared_on', {
                            date: getDateTime({
                                date: new Date(),
                                noTimeString: true
                            })
                        })}
                    </Box>
                </Box>
            </Box>
        );
    };

    render() {
        const { loaded, image, settings } = this.state;

        if (!loaded || !image) {
            return null;
        }

        return (
            <Box
                className="showOnlyInPrint"
                data-testid="coverPageMainContainer"
                data-trackid="coverPageMainContainer"
                style={{ pageBreakAfter: 'always', position: 'relative' }}
            >
                <Box
                    component="img"
                    sx={{
                        backgroundColor: `${
                            settings.brandEnabled && settings.brandHex
                                ? settings.brandHex
                                : 'cover.primary'
                        }`,
                        backgroundRepeat: 'no-repeat',
                        width: '100%'
                    }}
                    src={image}
                    id="coverPageCoverContainer"
                    data-testid="coverPageCoverContainer"
                    data-trackid="coverPageCoverContainer"
                />
                <Box
                    width="100%"
                    position="absolute"
                    sx={{ bottom: { lg: '5%', xs: 0 } }}
                    display="flex"
                    justifyContent="space-around"
                    mb={[0, '64px']}
                >
                    <Box>{this.renderPreparedBy()}</Box>
                    <Box>{this.renderPreparedFor()}</Box>
                </Box>
            </Box>
        );
    }
}

export default withRouter(withTranslation()(CoverPage));
